import http, { endpoints } from '../http';
import { IUserBranchDto } from './dtos';
import { getInitialsFromFullName, mapUserBranchFromDto, mapUserToDto } from './helpers';
import { IAcceptInviteParams, IAddNewUserParams, IBranch, IUser, IUserAccessChangeParams } from './models';
import { IBranchDto } from '../department/dtos';
import { mapBranchFromDto } from '../department/helpers';
import { ICommonResponse } from '../types';

const datahubClient = http.createClient(`${endpoints.datahub}`);

export default class UsersApi {
  static async getUsersForBranch(branchId: IBranch['id']) {
    const response = await datahubClient.get<IUserBranchDto[]>(`/User/by-branchId/${branchId}/active`);
    return response?.data ? response?.data.map(user => mapUserBranchFromDto(user, branchId)) : [];
  }

  static async getUserById(id: string, options?: {
    abortSignal?: AbortSignal
  }): Promise<Partial<IUser>> {
    const response = await datahubClient.get<{
      id: string
      email: string
      firstName: string
      lastName: string
      phone: string
      branches: IBranchDto[]
      settings: {
        showPrices: boolean
      }
    }>(`/User/id/${id}`, {
      signal: options?.abortSignal,
    });

    const fullName = response?.data ? `${response.data.firstName} ${response.data.lastName}` : '';

    return (
      response?.data && {
        id: String(response.data.id),
        firstName: response.data.firstName,
        lastName: response.data.lastName,
        phoneNumber: response.data.phone,
        fullName,
        initials: fullName ? getInitialsFromFullName(fullName) : '',
        email: response.data.email,
        isInternalUser: true, // all users are non-esoft
        branches: response.data.branches.map(mapBranchFromDto),
        visiblePrices: response.data.settings.showPrices,
      }
    );
  }

  static async updateUser(user: IUser) {
    const response = await datahubClient.patch(`/User/id/${user.id}`, mapUserToDto(user));
    return response?.data;
  }

  static async getContactPersonTypes() {
    const response = await datahubClient.get<
      {
        key: string
        i18n: {
          languageCode: string
          text: string
        }[]
      }[]
    >('/orders/contact-person-types');
    return response?.data ? response.data : [];
  }

  static async getPendingInvitesForBranch(branchId: IBranch['id']) {
    const response = await datahubClient.get<{ id: string; email: IUser['email'] }[]>(
      `/User/Invitation/branch/${branchId}`,
    );
    return response?.data || [];
  }

  static async sendInvite(params: IAddNewUserParams) {
    const response = await datahubClient.post<ICommonResponse>('/User/Invitation', params);
    return response?.data;
  }

  static async removePendingInvite(inviteId: string) {
    const response = await datahubClient.delete<ICommonResponse>(`/User/Invitation/${inviteId}`);
    return response?.data;
  }

  static async resendInvite(inviteId: string) {
    const response = await datahubClient.post<ICommonResponse>(`/User/Invitation/${inviteId}/resend`);
    return response?.data || {};
  }

  static async findInviteById(inviteId: string) {
    const response = await datahubClient.get<IAddNewUserParams & { id: string }>(`/User/Invitation/${inviteId}`);
    return response?.data;
  }

  static async acceptInvite(params: IAcceptInviteParams) {
    const response = await datahubClient.post<ICommonResponse>(`/User/Invitation/${params.id}/accept`, {
      firstName: params.firstName,
      lastName: params.lastName,
      mobilePhone: params.mobilePhone,
      password: params.password,
    });
    return response?.data;
  }

  static async revokeBranchAccessForUser(params: IUserAccessChangeParams) {
    const response = await datahubClient.delete<ICommonResponse>(`/User/id/${params.userId}/branch/${params.branchId}`);
    return response?.data || {};
  }

  static async updateBranchAccessForUser(params: IUserAccessChangeParams) {
    const response = await datahubClient.patch<ICommonResponse>(`/User/id/${params.userId}/branch/${params.branchId}`, {
      isAdmin: params.isAdmin,
    });
    return response?.data || {};
  }
}
